import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Card, Row, Col, Alert, Spinner, ButtonGroup, Modal, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import { createTrip, getPassengers, getAreas, getCostCenters, createArea, createCostCenter, getQuote, createPassenger, updatePassenger } from '../api';
import { Car, Info, Plane, User, Mail, Phone, Plus } from 'lucide-react';
import apiClient from '../../api'; 
import AddressAutocomplete from '../components/AddressAutocomplete';
import RouteMap from '../components/RouteMap';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import { toast } from 'react-toastify';

const AIRPORTS = [
    { code: 'SCL', name: 'SCL - Aeropuerto SCL, Pudahuel, Chile', address: 'Aeropuerto - Aeropuerto SCL, Pudahuel, Chile.', lat: -33.39732166748097, lng: -70.79443028467028 },
    { code: 'CJC', name: 'Aeropuerto El Loa (CJC), Calama', address: 'Aeropuerto El Loa, Calama, Chile', lat: -22.4981, lng: -68.9039 },
    { code: 'ANF', name: 'Aeropuerto Andrés Sabella (ANF), Antofagasta', address: 'Aeropuerto Andrés Sabella, Antofagasta, Chile', lat: -23.4444, lng: -70.4451 },
    { code: 'IQQ', name: 'Aeropuerto Diego Aracena (IQQ), Iquique', address: 'Aeropuerto Diego Aracena, Iquique, Chile', lat: -20.5353, lng: -70.1814 },
    { code: 'ARI', name: 'Aeropuerto Chacalluta (ARI), Arica', address: 'Aeropuerto Internacional Chacalluta, Arica, Chile', lat: -18.3485, lng: -70.3386 },
    { code: 'LSC', name: 'Aeródromo La Florida (LSC), La Serena', address: 'Aeródromo La Florida, La Serena, Chile', lat: -29.9161, lng: -71.2017 },
    { code: 'CCP', name: 'Aeropuerto Carriel Sur (CCP), Concepción', address: 'Aeropuerto Carriel Sur, Concepción, Chile', lat: -36.7727, lng: -73.0631 },
    { code: 'PMC', name: 'Aeropuerto El Tepual (PMC), Puerto Montt', address: 'Aeropuerto El Tepual, Puerto Montt, Chile', lat: -41.4389, lng: -73.0940 },
    { code: 'ZCO', name: 'Aeropuerto La Araucanía (ZCO), Temuco', address: 'Aeropuerto Internacional La Araucanía (ZCO), Freire, Araucanía, Chile.', lat: -38.9254, lng: -72.6517 },
    { code: 'MDZ', name: 'Aeropuerto El Plumerillo (MDZ), Mendoza', address: 'Aeropuerto El Plumerillo, Mendoza, Argentina', lat: -32.8317, lng: -68.7919 },
    { code: 'EZE', name: 'Aeropuerto Ezeiza (EZE), Buenos Aires', address: 'Aeropuerto Internacional Ezeiza, Buenos Aires, Argentina', lat: -34.815, lng: -58.5358 },
    { code: 'AEP', name: 'Aeroparque Jorge Newbery (AEP), Buenos Aires', address: 'Aeroparque Jorge Newbery, Buenos Aires, Argentina', lat: -34.558, lng: -58.4164 },
    { code: 'PUJ', name: 'Aeropuerto Punta Cana (PUJ)', address: 'Punta Cana International Airport, Punta Cana, República Dominicana', lat: 18.5674, lng: -68.3634 },
    { code: 'SDQ', name: 'Aeropuerto Las Américas (SDQ)', address: 'Aeropuerto Internacional Las Américas, Santo Domingo, República Dominicana', lat: 18.4297, lng: -69.6689 },
    { code: 'LIM', name: 'Aeropuerto Jorge Chávez (LIM)', address: 'Aeropuerto Internacional Jorge Chávez, Lima, Perú', lat: -12.0219, lng: -77.1143 },
    { code: 'BOG', name: 'Aeropuerto El Dorado (BOG)', address: 'Aeropuerto Internacional El Dorado, Bogotá, Colombia', lat: 4.7016, lng: -74.1469 }
];

const NewTripPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const editTrip = location.state?.editTrip || null;
  const isEditing = !!editTrip;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Data
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [availablePassengers, setAvailablePassengers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [clientData, setClientData] = useState(null); 
  const [userData, setUserData] = useState(null);
  const [minAdvanceMinutes, setMinAdvanceMinutes] = useState(0);

  // Modals for Quick Create
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [areaModalTarget, setAreaModalTarget] = useState('trip');
  const [showCCModal, setShowCCModal] = useState(false);
  const [showQuickPaxModal, setShowQuickPaxModal] = useState(false);
  const [quickPax, setQuickPax] = useState({
    id: null,
    full_name: '',
    email: '',
    phone: '',
    area_id: '',
    cost_center_id: '',
    home_address: '',
    office_address: '',
    has_portal_access: false
  });
  const [newItemName, setNewItemName] = useState('');
  const [newItemCode, setNewItemCode] = useState('');

  // Pasajero principal: autocomplete
  const [paxMainSearch, setPaxMainSearch] = useState('');
  const [showPaxDropdown, setShowPaxDropdown] = useState(false);

  const [paxSearchTerm, setPaxSearchTerm] = useState('');

  // Mensaje de éxito tras crear reserva
  const [successMessage, setSuccessMessage] = useState(null);
  const [successTripId, setSuccessTripId] = useState(null);
  // Estado para modo retorno
  const [isCreatingReturn, setIsCreatingReturn] = useState(false);

  const [stopPaxSearch, setStopPaxSearch] = useState({}); // { [stopIndex]: 'search string' }
  const [showStopPaxDropdown, setShowStopPaxDropdown] = useState({}); // { [stopIndex]: true/false }

  // Airport selection state
  const [showAirportModal, setShowAirportModal] = useState(false);
  const [airportTargetField, setAirportTargetField] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    passenger_id: '', 
    passenger_name: '',
    passenger_phone: '',
    
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    
    dropoff_address: '',
    dropoff_lat: null,
    dropoff_lng: null,

    scheduled_date: '',
    scheduled_time: '08:00',
    vehicle_type_id: '',
    notes: '',

    distance_km: 0,
    estimated_duration: '',
    estimated_duration_minutes: 0,

    area_id: '',
    cost_center_id: '',

    // NUEVOS CAMPOS FASE 4
    stops: [],
    additional_passenger_ids: [],
    expense_nature: '',
    client_file_number: '',
    flight_number: '',
    extra_services: []
  });

  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [paxMode, setPaxMode] = useState('self'); // 'self' | 'other'

  const isParticular = useMemo(() => {
    if (!clientData) return false;
    return clientData.segmentation === 'Particulares' || clientData.name?.toUpperCase().includes('PARTICULARES');
  }, [clientData]);

  useEffect(() => {
    const initData = async () => {
      try {
        let myClient = null;
        let myProfile = null;

        if (user.role === 'passenger' && user.linked_passenger_id) {
          const res = await apiClient.get(`/transport/passengers/${user.linked_passenger_id}`);
          myProfile = res.data;
          myClient = { id: myProfile.client_id }; 
          
          setFormData(prev => ({
            ...prev,
            passenger_id: myProfile.id,
            passenger_name: myProfile.full_name,
            passenger_phone: myProfile.phone || '',
            area_id: myProfile.area_id || '',
            cost_center_id: myProfile.cost_center_id || ''
          }));

          // Cargar otros pasajeros del cliente para acompañantes si no es particular
          const clientResTemp = await apiClient.get(`/transport/clients/${myClient.id}`);
          if (clientResTemp.data?.segmentation !== 'Particulares' && !clientResTemp.data?.name?.toUpperCase().includes('PARTICULARES')) {
            const others = await getPassengers(myClient.id);
            setAvailablePassengers(others);
          }

        } else if (user.role === 'requester' && user.linked_requester_id) {
          const res = await apiClient.get(`/transport/requesters/${user.linked_requester_id}`);
          myProfile = res.data;
          myClient = { id: myProfile.client_id };

          const clientResTemp = await apiClient.get(`/transport/clients/${myClient.id}`);
          const isPartTemp = clientResTemp.data?.segmentation === 'Particulares' || clientResTemp.data?.name?.toUpperCase().includes('PARTICULARES');

          if (!isPartTemp) {
            const [passengers, areaList, ccList] = await Promise.all([
                getPassengers(myClient.id),
                getAreas(myClient.id),
                getCostCenters(myClient.id)
            ]);
            setAvailablePassengers(passengers);
            setAreas(areaList);
            setCostCenters(ccList);
          } else {
            setAvailablePassengers([]);
            setAreas([]);
            setCostCenters([]);
            setFormData(prev => ({
              ...prev,
              passenger_id: 'self',
              passenger_name: myProfile?.full_name || user.full_name || '',
              passenger_phone: myProfile?.phone || user.phone || ''
            }));
          }
        }

        // Cargar datos completos del cliente para obtener min_lead_time_minutes
        if (myClient?.id) {
          try {
            const clientRes = await apiClient.get(`/transport/clients/${myClient.id}`);
            myClient = clientRes.data;
            setMinAdvanceMinutes(clientRes.data.min_lead_time_minutes || 0);
          } catch (e) {
            console.warn('No se pudo cargar configuración del cliente:', e);
          }
        }

        setClientData(myClient);
        setUserData(myProfile);
        setLoading(false);

      } catch (err) {
        console.error("Error cargando datos:", err);
        setError("No se pudo cargar la información. Verifique su conexión.");
        setLoading(false);
      }
    };

    if (user) {
        initData();
    }
  }, [user]);

  // Cargar datos de la reserva a modificar si viene desde el portal
  useEffect(() => {
    if (editTrip && !loading) {
      let dateStr = '';
      let timeStr = '08:00';
      if (editTrip.scheduled_time) {
        const d = new Date(editTrip.scheduled_time);
        const pad = (n) => String(n).padStart(2, '0');
        dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }

      setFormData(prev => ({
        ...prev,
        passenger_id: editTrip.passenger_id || 'self',
        passenger_name: editTrip.passenger_name || '',
        passenger_phone: editTrip.passenger_phone || '',
        pickup_address: editTrip.pickup_address || '',
        pickup_lat: editTrip.pickup_lat ? parseFloat(editTrip.pickup_lat) : null,
        pickup_lng: editTrip.pickup_lng ? parseFloat(editTrip.pickup_lng) : null,
        dropoff_address: editTrip.dropoff_address || '',
        dropoff_lat: editTrip.dropoff_lat ? parseFloat(editTrip.dropoff_lat) : null,
        dropoff_lng: editTrip.dropoff_lng ? parseFloat(editTrip.dropoff_lng) : null,
        scheduled_date: dateStr,
        scheduled_time: timeStr,
        vehicle_type_id: editTrip.requested_vehicle_type_id || editTrip.vehicle_type_id || '',
        notes: editTrip.notes || '',
        area_id: editTrip.area_id || '',
        cost_center_id: editTrip.cost_center_id || '',
        flight_number: editTrip.flight_number || '',
        client_file_number: editTrip.client_file_number || editTrip.file || '',
        stops: editTrip.stops || [],
        extra_services: editTrip.extra_services || []
      }));

      if (editTrip.passenger_name) {
        setPaxMainSearch(editTrip.passenger_name);
        setPaxMode('other');
      }
    }
  }, [editTrip, loading]);

  // Obtener cotizaciones en tiempo real
  const fetchQuotes = useCallback(async () => {
    const { pickup_lat, pickup_lng, dropoff_lat, dropoff_lng, scheduled_date, scheduled_time, additional_passenger_ids } = formData;
    
    if (!pickup_lat || !pickup_lng || !dropoff_lat || !dropoff_lng || !scheduled_date || !scheduled_time) {
      setAvailableQuotes([]);
      return;
    }

    setLoadingQuotes(true);
    try {
      const scheduledDateTime = `${scheduled_date}T${scheduled_time}:00`;
      const stopsPaxCount = (formData.stops || []).filter(s => s.passenger_name && s.passenger_name.trim() !== '').length;
      const quotes = await getQuote({
        pickup_lat,
        pickup_lng,
        dropoff_lat,
        dropoff_lng,
        client_id: clientData?.id,
        scheduled_time: scheduledDateTime,
        distance_km: formData.distance_km,
        passengers: additional_passenger_ids.length + 1 + stopsPaxCount,
        suitcases: 0
      });
      
      setAvailableQuotes(Array.isArray(quotes) ? quotes : []);
    } catch (e) {
      console.error("Error al cotizar viaje corporativo:", e);
      setAvailableQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pickup_lat, formData.pickup_lng, formData.dropoff_lat, formData.dropoff_lng, formData.scheduled_date, formData.scheduled_time, formData.additional_passenger_ids.length, formData.distance_km, clientData?.id, (formData.stops || []).filter(s => s.passenger_name && s.passenger_name.trim() !== '').length]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const dateTimeValidationError = useMemo(() => {
      if (!formData.scheduled_date || !formData.scheduled_time) return null;
      const [year, month, day] = formData.scheduled_date.split('-').map(Number);
      const [hour, minute] = formData.scheduled_time.split(':').map(Number);
      const scheduledDateTimeMs = new Date(year, month - 1, day, hour, minute, 0).getTime();
      const nowMs = Date.now();
      if (scheduledDateTimeMs <= nowMs) {
          return "La fecha y hora del servicio no puede ser en el pasado.";
      }
      if (minAdvanceMinutes > 0) {
          const minMs = nowMs + minAdvanceMinutes * 60 * 1000;
          if (scheduledDateTimeMs < minMs) {
              const horas = Math.floor(minAdvanceMinutes / 60);
              const mins = minAdvanceMinutes % 60;
              const label = horas > 0
                  ? `${horas}h${mins > 0 ? ` ${mins}min` : ''}`
                  : `${mins} minutos`;
              return `Su empresa requiere una anticipación mínima de ${label} para solicitar un servicio.`;
          }
      }
      return null;
  }, [formData.scheduled_date, formData.scheduled_time, minAdvanceMinutes]);


  // Seleccionar pasajero principal desde el autocomplete
  const handleSelectMainPassenger = (p) => {
      setFormData(prev => ({
          ...prev,
          passenger_id: p === 'self' ? 'self' : p.id,
          passenger_name: p === 'self' ? userData.full_name : p.full_name,
          passenger_phone: p === 'self' ? (userData.phone || '') : (p.phone || ''),
          area_id: p === 'self' ? (userData.area_id || '') : (p.area_id || ''),
          cost_center_id: p === 'self' ? (userData.cost_center_id || '') : (p.cost_center_id || ''),
          additional_passenger_ids: prev.additional_passenger_ids.filter(
              id => id !== (p === 'self' ? userData?.id : p.id)
          )
      }));
      setPaxMainSearch(p === 'self' ? userData.full_name : p.full_name);
      setShowPaxDropdown(false);
  };

  // handlePassengerChange mantenido por compatibilidad interna (no usado en JSX)
  // eslint-disable-next-line no-unused-vars
  const handlePassengerChange = (e) => {
      const val = e.target.value;
      if (val === 'self') handleSelectMainPassenger('self');
      else {
          const found = availablePassengers.find(p => p.id === parseInt(val));
          if (found) handleSelectMainPassenger(found);
      }
  };

  const handleToggleAcompanante = (paxId) => {
      setFormData(prev => {
          const current = prev.additional_passenger_ids;
          const updated = current.includes(paxId) ? current.filter(id => id !== paxId) : [...current, paxId];
          return { ...prev, additional_passenger_ids: updated };
      });
  };

  const handleTimeHourChange = (e) => {
      const hr = e.target.value;
      const [, selectedMinute] = (formData.scheduled_time || '08:00').split(':');
      const min = selectedMinute || '00';
      setFormData(prev => ({
          ...prev,
          scheduled_time: `${hr}:${min}`
      }));
  };

  const handleTimeMinuteChange = (e) => {
      const [selectedHour] = (formData.scheduled_time || '08:00').split(':');
      const hr = selectedHour || '08';
      const min = e.target.value;
      setFormData(prev => ({
          ...prev,
          scheduled_time: `${hr}:${min}`
      }));
  };

  const handleEditMainPassenger = () => {
      const p = availablePassengers.find(cp => cp.id === parseInt(formData.passenger_id));
      if (p) {
          setQuickPax({
              id: p.id,
              full_name: p.full_name || '',
              email: p.email || '',
              phone: p.phone || '',
              area_id: p.area_id || '',
              cost_center_id: p.cost_center_id || '',
              home_address: p.home_address || '',
              office_address: p.office_address || '',
              has_portal_access: p.has_portal_access || false
          });
          setShowQuickPaxModal(true);
      }
  };

  const EXTRA_OPTIONS = [
    { id: 'baby_seat',     name: '👶 Silla de Bebé / Booster', extraPrice: 5000 },
    { id: 'water',         name: '💧 Agua Mineral',              extraPrice: 1500 },
    { id: 'pet',           name: '🐾 Mascota Pequeña',           extraPrice: 0    },
    { id: 'extra_luggage', name: '🧳 Maleta Extra',              extraPrice: 0    }
  ];

  const handleToggleExtra = (id, name) => {
      setFormData(prev => {
          const current = prev.extra_services || [];
          const exists = current.find(s => s.id === id);
          let updated;
          if (exists) {
              updated = current.filter(s => s.id !== id);
          } else {
              updated = [...current, { id, name, price: 0 }];
          }
          return { ...prev, extra_services: updated };
      });
  };

  const handleAddStop = () => {
      setFormData(prev => ({ ...prev, stops: [...prev.stops, { address: '', lat: null, lng: null, passenger_name: null, passenger_phone: null }] }));
  };

  const handleRemoveStop = (idx) => {
      setFormData(prev => ({ ...prev, stops: prev.stops.filter((_, i) => i !== idx) }));
  };

  const optimizeStopsOrder = () => {
      if (!formData.stops || formData.stops.length <= 1 || !formData.pickup_lat || !formData.pickup_lng) {
          return;
      }

      const validStops = [];
      const invalidStops = [];
      formData.stops.forEach(s => {
          if (s.lat !== null && s.lat !== undefined && s.lng !== null && s.lng !== undefined) {
              validStops.push({ ...s });
          } else {
              invalidStops.push({ ...s });
          }
      });

      if (validStops.length === 0) return;

      const getDistance = (lat1, lng1, lat2, lng2) => {
          return Math.hypot(lat1 - lat2, lng1 - lng2);
      };

      const sortedStops = [];
      let currentLat = formData.pickup_lat;
      let currentLng = formData.pickup_lng;

      while (validStops.length > 0) {
          let closestIdx = 0;
          let minDist = getDistance(currentLat, currentLng, validStops[0].lat, validStops[0].lng);
          for (let i = 1; i < validStops.length; i++) {
              const dist = getDistance(currentLat, currentLng, validStops[i].lat, validStops[i].lng);
              if (dist < minDist) {
                  minDist = dist;
                  closestIdx = i;
              }
          }

          const closestStop = validStops.splice(closestIdx, 1)[0];
          sortedStops.push(closestStop);
          currentLat = closestStop.lat;
          currentLng = closestStop.lng;
      }

      const finalStops = [...sortedStops, ...invalidStops];
      setFormData(prev => ({ ...prev, stops: finalStops }));
      toast.success("Ruta de paradas optimizada logísticamente.");
  };

  const handleStopSelect = (idx, { address, lat, lng }) => {
      const newStops = [...formData.stops];
      newStops[idx] = { address, lat, lng };
      setFormData({ ...formData, stops: newStops });
  };

  const handleCreateArea = async () => {
      if (!newItemName) return;
      try {
          const newArea = await createArea({ name: newItemName, client_id: clientData.id });
          setAreas([...areas, newArea]);
          if (areaModalTarget === 'trip') {
              setFormData(prev => ({ ...prev, area_id: newArea.id }));
          } else if (areaModalTarget === 'pax') {
              setQuickPax(prev => ({ ...prev, area_id: newArea.id }));
          }
          setShowAreaModal(false);
          setNewItemName('');
      } catch (e) { alert('Error creando área'); }
  };

  // Target guardado para saber dónde asignar el CC creado (trip o pax)
  const [ccModalTarget, setCcModalTarget] = useState('trip');

  const openCCModal = (target = 'trip') => {
      setCcModalTarget(target);
      setNewItemCode('');
      setNewItemName('');
      setShowCCModal(true);
  };

  const handleCreateCCForTarget = async () => {
      if (!newItemCode) return;
      try {
          const newCC = await createCostCenter({ code: newItemCode, name: newItemName, client_id: clientData.id });
          setCostCenters(prev => [...prev, newCC]);
          if (ccModalTarget === 'trip') {
              setFormData(prev => ({ ...prev, cost_center_id: newCC.id }));
          } else if (ccModalTarget === 'pax') {
              setQuickPax(prev => ({ ...prev, cost_center_id: newCC.id }));
          }
          setShowCCModal(false);
          setNewItemCode(''); setNewItemName('');
      } catch (e) { alert('Error creando centro de costo'); }
  };

  const handleQuickCreatePassenger = async () => {
      try {
          const payload = { ...quickPax, client_id: clientData?.id, has_portal_access: false };
          if (payload.area_id === '') payload.area_id = null;
          if (payload.cost_center_id === '') payload.cost_center_id = null;
          if (payload.home_address === '') payload.home_address = null;
          if (payload.office_address === '') payload.office_address = null;
          
          let res;
          if (quickPax.id) {
              res = await updatePassenger(quickPax.id, payload);
          } else {
              res = await createPassenger(payload);
          }
          
          const updatedPax = await getPassengers(clientData.id);
          setAvailablePassengers(updatedPax);

          // FIX 4: Solo asignar como principal si aún no hay pasajero elegido, o si lo estamos editando
          if (!formData.passenger_id || quickPax.id === parseInt(formData.passenger_id)) {
              setFormData(prev => ({
                  ...prev,
                  passenger_id: res.id,
                  passenger_name: res.full_name,
                  passenger_phone: res.phone || '',
                  area_id: res.area_id || prev.area_id || '',
                  cost_center_id: res.cost_center_id || prev.cost_center_id || ''
              }));
              setPaxMainSearch(res.full_name);
          }
          // Si ya hay principal, el nuevo queda disponible en acompañantes automáticamente

          setShowQuickPaxModal(false);
          setQuickPax({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false });
      } catch (e) {
          console.error("Error creating passenger:", e);
          alert(quickPax.id ? 'Error al actualizar pasajero' : 'Error al crear pasajero');
      }
  };

  const openAreaModal = (target = 'trip') => {
      setAreaModalTarget(target);
      setNewItemName('');
      setShowAreaModal(true);
  };

  const handlePickupSelect = useCallback(({ address, lat, lng }) => {
      setFormData(prev => ({ ...prev, pickup_address: address, pickup_lat: lat, pickup_lng: lng }));
  }, []);

  const handleDropoffSelect = useCallback(({ address, lat, lng }) => {
      setFormData(prev => ({ ...prev, dropoff_address: address, dropoff_lat: lat, dropoff_lng: lng }));
  }, []);

  const parseDurationToMinutes = (durationStr) => {
    if (durationStr === null || durationStr === undefined) return 0;
    if (typeof durationStr === 'number') return durationStr;
    if (typeof durationStr !== 'string') return 0;
    let total = 0;
    const hourMatch = durationStr.match(/(\d+)\s*hour/);
    const minMatch  = durationStr.match(/(\d+)\s*min/);
    if (hourMatch) total += parseInt(hourMatch[1]) * 60;
    if (minMatch)  total += parseInt(minMatch[1]);
    return total || 0;
  };

  const handleDistanceCalculated = useCallback((dist, duration) => {
      setFormData(prev => {
          if (Math.abs(prev.distance_km - dist) < 0.01) return prev;
          const displayDuration = typeof duration === 'number' ? `${duration} min` : duration;
          return { ...prev, distance_km: dist, estimated_duration: displayDuration, estimated_duration_minutes: parseDurationToMinutes(duration) };
      });
  }, []);

  const geocodeAndSet = async (address, fieldPrefix) => {
      if (!address) return;
      setFormData(prev => ({ ...prev, [`${fieldPrefix}_address`]: address }));
      try {
          const results = await getGeocode({ address });
          if (results && results.length > 0) {
              const { lat, lng } = await getLatLng(results[0]);
              setFormData(prev => ({ 
                  ...prev, 
                  [`${fieldPrefix}_address`]: address,
                  [`${fieldPrefix}_lat`]: lat, 
                  [`${fieldPrefix}_lng`]: lng 
              }));
          } else {
              console.warn("No se encontraron coordenadas para:", address);
          }
      } catch (error) {
          console.error("Error geocodificando dirección:", error);
      }
  };

  const setQuickAddress = (type, target) => {
      if (type === 'airport') {
          setAirportTargetField(target);
          setShowAirportModal(true);
          return;
      }
      
      let address = "";
      const selectedPaxId = formData.passenger_id;
      
      if (!selectedPaxId || selectedPaxId === 'self') {
          if (type === 'home') address = userData?.home_address;
          if (type === 'office') address = userData?.office_address;
      } else {
          const p = availablePassengers.find(cp => cp.id === parseInt(selectedPaxId));
          if (p) {
              if (type === 'home') address = p.home_address;
              if (type === 'office') address = p.office_address;
          } else {
              if (type === 'home') address = userData?.home_address;
              if (type === 'office') address = userData?.office_address;
          }
      }

      if (address) {
          geocodeAndSet(address, target);
      } else {
          alert(`No tienes dirección de ${type === 'home' ? 'Casa' : 'Oficina'} guardada.`);
      }
  };

  const handleAirportSelect = (airport) => {
      const { name, address, lat, lng } = airport;
      const finalAddress = address || name;
      setShowAirportModal(false);
      
      setFormData(prev => ({ 
          ...prev, 
          [`${airportTargetField}_address`]: finalAddress, 
          [`${airportTargetField}_lat`]: lat, 
          [`${airportTargetField}_lng`]: lng 
      }));
  };

  const isAirport = (address) => {
      if (!address || typeof address !== 'string') return false;
      const lower = address.toLowerCase();
      const unambiguous = ['aeropuerto', 'airport', 'arturo merino benitez', 'pudahuel', 'chacalluta', 'el tepual', 'carriel sur', 'el plumerillo', 'ezeiza', 'aeroparque', 'terminal 1', 'terminal 2'];
      if (unambiguous.some(k => lower.includes(k))) return true;
      const iataCodes = ['scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'pmc', 'zco', 'ari', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim', 'bog', 'dorado', 'cpo', 'araucania'];
      return iataCodes.some(code => {
          const regex = new RegExp(`(^|[^a-z0-9])${code}([^a-z0-9]|$)`, 'i');
          return regex.test(lower);
      });
  };

  const isPickupAirport = isAirport(formData.pickup_address);
  const isDropoffAirport = isAirport(formData.dropoff_address);
  const isServiceFarAhead = useMemo(() => {
      if (!formData.scheduled_date) return false;
      const now = new Date();
      const svcDate = new Date(formData.scheduled_date + 'T00:00:00');
      const diffDays = (svcDate - now) / (1000 * 60 * 60 * 24);
      return diffDays > 2;
  }, [formData.scheduled_date]);
  const flightRequired = isPickupAirport && !isServiceFarAhead;

  const handleSubmit = async (e, shouldCreateReturn = false) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!formData.passenger_name || !formData.passenger_name.trim()) {
        setError("Por favor ingrese el nombre del pasajero principal.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (!formData.scheduled_date) {
        setError("Por favor seleccione la fecha del servicio.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (!formData.scheduled_time) {
        setError("Por favor ingrese la hora del servicio.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (!formData.pickup_address || !formData.dropoff_address) {
        setError("Por favor ingrese las direcciones de origen y destino.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (!formData.vehicle_type_id) {
        setError("Por favor seleccione el tipo de vehículo.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    const [year, month, day] = formData.scheduled_date.split('-').map(Number);
    const [hour, minute] = formData.scheduled_time.split(':').map(Number);
    const scheduledDateTimeMs = new Date(year, month - 1, day, hour, minute, 0).getTime();
    const nowMs = Date.now();

    if (scheduledDateTimeMs <= nowMs) {
        setError("La fecha y hora del servicio no puede ser en el pasado.");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    if (minAdvanceMinutes > 0) {
        const minMs = nowMs + minAdvanceMinutes * 60 * 1000;
        if (scheduledDateTimeMs < minMs) {
            const horas = Math.floor(minAdvanceMinutes / 60);
            const mins = minAdvanceMinutes % 60;
            const label = horas > 0
                ? `${horas}h${mins > 0 ? ` ${mins}min` : ''}`
                : `${mins} minutos`;
            setError(`Su empresa requiere una anticipación mínima de ${label} para solicitar un servicio. Por favor elija una fecha/hora más adelante.`);
            setSubmitting(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
    }

    if (flightRequired && !formData.flight_number?.trim()) {
        setError("El número de vuelo es obligatorio para traslados desde el aeropuerto (recogida en el aeropuerto).");
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    const scheduledDateTime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;

    const payload = {
        client_id: clientData.id,
        requester_id: user.role === 'requester' ? (userData?.id || user.linked_requester_id || null) : null,
        
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        passenger_count: formData.additional_passenger_ids.length + 1 + (formData.stops || []).filter(s => s.passenger_name && s.passenger_name.trim() !== '').length, 
        
        requested_vehicle_type_id: parseInt(formData.vehicle_type_id),
        
        pickup_address: formData.pickup_address,
        pickup_lat: formData.pickup_lat,
        pickup_lng: formData.pickup_lng,
        
        dropoff_address: formData.dropoff_address,
        dropoff_lat: formData.dropoff_lat,
        dropoff_lng: formData.dropoff_lng,
        
        distance_km: formData.distance_km,
        estimated_duration_minutes: formData.estimated_duration_minutes || 0,
        scheduled_time: scheduledDateTime,
        notes: formData.notes,
        flight_number: formData.flight_number || null,
        
        area_id: formData.area_id ? parseInt(formData.area_id) : null,
        cost_center_id: formData.cost_center_id ? parseInt(formData.cost_center_id) : null,

        stops: formData.stops,
        additional_passenger_ids: formData.additional_passenger_ids,
        expense_nature: null,
        file: formData.client_file_number || null,
        client_file_number: formData.client_file_number || null,
        extra_services: formData.extra_services || [],
        trip_source: 'web'
    };

    try {
        // Verificar viajes duplicados antes de guardar
        try {
            const checkRes = await apiClient.post('/transport/trips/check-duplicates', {
                client_id: parseInt(clientData.id),
                scheduled_time: scheduledDateTime,
                passenger_name: formData.passenger_name,
                external_reference: formData.client_file_number || null,
                trip_id: isEditing ? editTrip.id : null,
                pickup_address: formData.pickup_address || null,
                dropoff_address: formData.dropoff_address || null,
                pickup_lat: formData.pickup_lat || null,
                pickup_lng: formData.pickup_lng || null
            });
            
            if (checkRes.data && checkRes.data.has_duplicate) {
                const confirmMsg = `⚠️ ADVERTENCIA DE DUPLICADO:\n\n${checkRes.data.reason}\n\n¿Estás seguro de que deseas solicitar esta reserva de todos modos?`;
                if (!window.confirm(confirmMsg)) {
                    setSubmitting(false);
                    return;
                }
            }
        } catch (checkErr) {
            console.error("Error al verificar duplicados en el backend:", checkErr);
        }

        if (isEditing) {
            await apiClient.put(`/transport/trips/${editTrip.id}`, payload);
            setSuccessTripId(editTrip.id);
            setSuccessMessage(`Tu reserva #${editTrip.id} fue modificada exitosamente. Serás redirigido en unos segundos...`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => navigate('/portal/my-trips'), 2500);
            return;
        }

        const res = await createTrip(payload);
        setSuccessTripId(res.id);

        if (shouldCreateReturn) {
            // Modo retorno: invertir direcciones y resetear para el segundo servicio
            setSuccessMessage(`Ida #${res.id} guardada. Ahora complete los datos del RETORNO.`);
            setIsCreatingReturn(true);
            setFormData(prev => ({
                ...prev,
                // Invertir origen y destino
                pickup_address: prev.dropoff_address,
                pickup_lat: prev.dropoff_lat,
                pickup_lng: prev.dropoff_lng,
                dropoff_address: prev.pickup_address,
                dropoff_lat: prev.pickup_lat,
                dropoff_lng: prev.pickup_lng,
                // Inicializar fecha limpia pero hora en 08:00 para coincidir con la UI
                scheduled_date: '',
                scheduled_time: '08:00',
                flight_number: '',
                notes: '',
                stops: [],
                extra_services: []
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setSuccessMessage(`Tu reserva #${res.id} fue creada exitosamente. Serás redirigido en unos segundos...`);
            setIsCreatingReturn(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => navigate('/portal/my-trips'), 3000);
        }
    } catch (err) {
        console.error(err);
        setError("Error al solicitar el viaje.");
    } finally {
        setSubmitting(false);
    }
  };

  if (loading) {
      return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  }

  const QuickButtons = ({ target }) => (
      <div className="mb-2">
          <ButtonGroup size="sm">
              <Button variant="outline-secondary" onClick={() => setQuickAddress('home', target)}>🏠 Casa</Button>
              <Button variant="outline-secondary" onClick={() => setQuickAddress('office', target)}>🏢 Oficina</Button>
              <Button variant="outline-secondary" onClick={() => setQuickAddress('airport', target)}>✈️ Aeropuerto</Button>
          </ButtonGroup>
      </div>
  );

  return (
    <div className="py-4">
      <Row>
        <Col lg={6}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className={isEditing ? "bg-warning text-dark border-bottom-0 pt-4" : "bg-white border-bottom-0 pt-4"}>
              <h4>{isEditing ? `✏️ Modificar Reserva #${editTrip.id}` : '✨ Solicitar Nuevo Viaje'}</h4>
              {isEditing && (
                <div className="small fw-bold text-dark mt-1">
                  💡 <strong>Modo Edición:</strong> Modifique origen, destino, fecha o tipo de vehículo. La tarifa se recotizará automáticamente en tiempo real.
                </div>
              )}
            </Card.Header>
            <Card.Body>
              {successMessage && (
                <Alert variant={isCreatingReturn ? 'warning' : 'success'} className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.5rem' }}>{isCreatingReturn ? '🔄' : '✅'}</span>
                  <div>
                    <div className="fw-bold">
                      {isCreatingReturn
                        ? `Ida #${successTripId} Creada con Éxito — Ahora ingresa el Retorno`
                        : `Reserva #${successTripId} Creada con Éxito`
                      }
                    </div>
                    <div>{successMessage}</div>
                  </div>
                </Alert>
              )}
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={handleSubmit}>
                <h6 className="text-muted mb-3 mt-2">Pasajeros</h6>
                {isParticular ? (
                    <Form.Group className="mb-4 p-3 border rounded bg-white shadow-sm">
                        <Form.Label className="fw-bold text-dark mb-2">¿Para quién es el servicio?</Form.Label>
                        <div className="d-flex gap-2 mb-3">
                            <Button
                                type="button"
                                variant={paxMode === 'self' ? 'primary' : 'outline-secondary'}
                                size="sm"
                                className="fw-bold"
                                onClick={() => {
                                    setPaxMode('self');
                                    setFormData(prev => ({
                                        ...prev,
                                        passenger_id: 'self',
                                        passenger_name: userData?.full_name || user.full_name || '',
                                        passenger_phone: userData?.phone || user.phone || ''
                                    }));
                                }}
                            >
                                👤 Para mí ({userData?.full_name || user.full_name})
                            </Button>
                            <Button
                                type="button"
                                variant={paxMode === 'other' ? 'primary' : 'outline-secondary'}
                                size="sm"
                                className="fw-bold"
                                onClick={() => {
                                    setPaxMode('other');
                                    setFormData(prev => ({
                                        ...prev,
                                        passenger_id: null,
                                        passenger_name: '',
                                        passenger_phone: ''
                                    }));
                                }}
                            >
                                👥 Para otra persona
                            </Button>
                        </div>

                        {paxMode === 'other' ? (
                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Label className="small text-muted mb-1">Nombre del Pasajero *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Ej: Juan Pérez"
                                        value={formData.passenger_name}
                                        onChange={e => setFormData(prev => ({ ...prev, passenger_name: e.target.value }))}
                                        required
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small text-muted mb-1">Teléfono del Pasajero</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Ej: +56 9 1234 5678"
                                        value={formData.passenger_phone}
                                        onChange={e => setFormData(prev => ({ ...prev, passenger_phone: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <div className="p-2 border rounded bg-light small">
                                <strong>Pasajero Principal:</strong> {formData.passenger_name || 'Cargando...'} {formData.passenger_phone && `(${formData.passenger_phone})`}
                            </div>
                        )}
                    </Form.Group>
                ) : user.role === 'passenger' ? (
                    <Form.Group className="mb-3">
                        <Form.Control type="text" value={formData.passenger_name} disabled readOnly className="bg-light" />
                        <Form.Text className="text-muted">Viaje personal</Form.Text>
                    </Form.Group>
                ) : (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Pasajero Principal</Form.Label>
                            <div className="position-relative">
                                <InputGroup>
                                    <InputGroup.Text>👤</InputGroup.Text>
                                    <Form.Control
                                        placeholder="Buscar pasajero..."
                                        value={paxMainSearch}
                                        onChange={e => { setPaxMainSearch(e.target.value); setShowPaxDropdown(true); }}
                                        onFocus={() => setShowPaxDropdown(true)}
                                        autoComplete="off"
                                    />
                                    <Button variant="outline-secondary" title="Nuevo Pasajero" onClick={() => {
                                        setQuickPax({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false });
                                        setShowQuickPaxModal(true);
                                    }}>+</Button>
                                </InputGroup>
                                {showPaxDropdown && (
                                    <div
                                        className="position-absolute bg-white border rounded shadow-sm w-100"
                                        style={{ zIndex: 1050, maxHeight: '220px', overflowY: 'auto', top: '100%' }}
                                    >
                                        {(!paxMainSearch || userData?.full_name?.toLowerCase().includes(paxMainSearch.toLowerCase())) && (
                                            <div
                                                className="px-3 py-2 hover-bg-light border-bottom"
                                                style={{ cursor: 'pointer', backgroundColor: formData.passenger_id === 'self' ? '#e8f4fd' : '' }}
                                                onClick={() => handleSelectMainPassenger('self')}
                                                onMouseDown={e => e.preventDefault()}
                                            >
                                                👤 Yo mismo ({userData?.full_name})
                                            </div>
                                        )}
                                        {availablePassengers
                                            .filter(p => !paxMainSearch || p.full_name.toLowerCase().includes(paxMainSearch.toLowerCase()))
                                            .map(p => (
                                                <div
                                                    key={p.id}
                                                    className="px-3 py-2"
                                                    style={{ cursor: 'pointer', backgroundColor: formData.passenger_id === p.id ? '#e8f4fd' : '' }}
                                                    onClick={() => handleSelectMainPassenger(p)}
                                                    onMouseDown={e => e.preventDefault()}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = formData.passenger_id === p.id ? '#e8f4fd' : ''}
                                                >
                                                    {p.full_name}
                                                    {p.phone && <span className="text-muted small ms-2">{p.phone}</span>}
                                                </div>
                                            ))
                                        }
                                        {paxMainSearch && !availablePassengers.some(p => p.full_name.toLowerCase().includes(paxMainSearch.toLowerCase())) && (
                                            <div className="px-3 py-2 text-muted small">Sin resultados para "{paxMainSearch}"</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {formData.passenger_name && (
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <Form.Text className="text-success">✓ {formData.passenger_name} seleccionado</Form.Text>
                                    {formData.passenger_id && formData.passenger_id !== 'self' && (
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="text-decoration-none p-0 small" 
                                            onClick={handleEditMainPassenger}
                                        >
                                            📝 Editar Pasajero
                                        </Button>
                                    )}
                                </div>
                            )}
                        </Form.Group>
                        {availablePassengers.length > 0 && (
                            <div className="mb-4 p-2 border rounded bg-light bg-opacity-50">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="small fw-bold text-muted mb-0">Acompañantes adicionales</Form.Label>
                                    <Form.Control 
                                        size="sm" 
                                        type="text" 
                                        placeholder="🔍 Buscar acompañante..." 
                                        style={{ maxWidth: '200px' }}
                                        value={paxSearchTerm}
                                        onChange={e => setPaxSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="p-2 bg-white rounded border" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {availablePassengers
                                        .filter(p => p.id !== parseInt(formData.passenger_id) && p.id !== userData?.id)
                                        .filter(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            const matchesSearch = paxSearchTerm && p.full_name.toLowerCase().includes(paxSearchTerm.toLowerCase());
                                            return isChecked || matchesSearch;
                                        })
                                        .map(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            return (
                                                <div key={p.id} className="d-flex align-items-center mb-1">
                                                    <Form.Check 
                                                        type="checkbox"
                                                        id={`pax-check-${p.id}`}
                                                        label={p.phone ? `${p.full_name} (${p.phone})` : p.full_name}
                                                        checked={isChecked}
                                                        onChange={() => handleToggleAcompanante(p.id)}
                                                        className="small mb-0"
                                                    />
                                                </div>
                                            );
                                        })
                                    }
                                    {availablePassengers
                                        .filter(p => p.id !== parseInt(formData.passenger_id) && p.id !== userData?.id)
                                        .filter(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            const matchesSearch = paxSearchTerm && p.full_name.toLowerCase().includes(paxSearchTerm.toLowerCase());
                                            return isChecked || matchesSearch;
                                        }).length === 0 && (
                                            <div className="text-muted small text-center py-2">
                                                {paxSearchTerm ? "No se encontraron resultados" : "Busca pasajeros por su nombre para agregarlos."}
                                            </div>
                                        )
                                    }
                                </div>
                                <div className="small mt-1 text-primary">Total Pasajeros: {formData.additional_passenger_ids.length + 1 + (formData.stops || []).filter(s => s.passenger_name && s.passenger_name.trim() !== '').length}</div>
                            </div>
                        )}
                    </>
                )}

                {user.role === 'requester' && !isParticular && (
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Área</Form.Label>
                                <InputGroup>
                                    <Form.Select value={formData.area_id} onChange={e => setFormData({...formData, area_id: e.target.value})}>
                                        <option value="">-- Seleccione --</option>
                                        {areas
                                            .filter(a => a.is_active !== false || String(a.id) === String(formData.area_id))
                                            .map(a => (
                                                <option key={a.id} value={a.id}>
                                                    {a.name} {a.is_active === false ? " (Inactivo)" : ""}
                                                </option>
                                            ))
                                        }
                                    </Form.Select>
                                    <Button variant="outline-secondary" onClick={() => openAreaModal('trip')}>+</Button>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Centro de Costo</Form.Label>
                                <InputGroup>
                                    <Form.Select value={formData.cost_center_id} onChange={e => setFormData({...formData, cost_center_id: e.target.value})}>
                                        <option value="">-- Seleccione --</option>
                                        {costCenters
                                            .filter(c => c.is_active !== false || String(c.id) === String(formData.cost_center_id))
                                            .map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.code} - {c.name} {c.is_active === false ? " (Inactivo)" : ""}
                                                </option>
                                            ))
                                        }
                                    </Form.Select>
                                    <Button variant="outline-secondary" onClick={() => openCCModal('trip')}>+</Button>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <h6 className="text-muted mb-3 mt-4">Ruta</h6>
                
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-end mb-1">
                      <Form.Label className="mb-0">Dirección de Origen <span className="text-danger">*</span></Form.Label>
                      <QuickButtons target="pickup" />
                  </div>
                  <AddressAutocomplete 
                      value={formData.pickup_address}
                      onChange={(val) => setFormData(prev => ({ ...prev, pickup_address: val }))}
                      onSelect={handlePickupSelect}
                      placeholder="Ej: Av. Apoquindo 4500"
                  />
                </Form.Group>

                {formData.stops.map((stop, index) => (
                    <Form.Group className="mb-3 ms-4" key={index}>
                        <InputGroup size="sm">
                            <InputGroup.Text>P{index+1}</InputGroup.Text>
                            <div className="flex-grow-1">
                                <AddressAutocomplete 
                                    value={stop.address}
                                    onChange={(val) => {
                                        const newStops = [...formData.stops];
                                        newStops[index].address = val;
                                        setFormData({ ...formData, stops: newStops });
                                    }}
                                    onSelect={(p) => handleStopSelect(index, p)}
                                    placeholder="Parada intermedia..."
                                />
                            </div>
                            <Button variant="outline-danger" onClick={() => handleRemoveStop(index)}>✕</Button>
                        </InputGroup>
                        <div className="mt-1 d-flex flex-column gap-1 bg-light p-2 rounded border border-light-subtle position-relative">
                            {stop.passenger_name ? (
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="small text-success fw-bold">
                                        👤 {stop.passenger_name} {stop.passenger_phone ? `(${stop.passenger_phone})` : ''}
                                    </span>
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="text-danger p-0 text-decoration-none small"
                                        onClick={() => {
                                            const newStops = [...formData.stops];
                                            newStops[index].passenger_name = null;
                                            newStops[index].passenger_phone = null;
                                            setFormData({ ...formData, stops: newStops });
                                        }}
                                    >
                                        Quitar Pasajero
                                    </Button>
                                </div>
                            ) : (
                                <div className="position-relative">
                                    <InputGroup size="sm">
                                        <InputGroup.Text className="bg-transparent border-end-0 text-muted">👤</InputGroup.Text>
                                        <Form.Control
                                            placeholder="Buscar y asignar pasajero..."
                                            value={stopPaxSearch[index] || ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setStopPaxSearch(prev => ({ ...prev, [index]: val }));
                                                setShowStopPaxDropdown(prev => ({ ...prev, [index]: true }));
                                            }}
                                            onFocus={() => setShowStopPaxDropdown(prev => ({ ...prev, [index]: true }))}
                                            onBlur={() => setTimeout(() => setShowStopPaxDropdown(prev => ({ ...prev, [index]: false })), 200)}
                                            autoComplete="off"
                                            className="border-start-0 text-dark"
                                        />
                                    </InputGroup>
                                    {showStopPaxDropdown[index] && (
                                        <div
                                            className="position-absolute bg-white border rounded shadow-sm w-100"
                                            style={{ zIndex: 1050, maxHeight: '180px', overflowY: 'auto', top: '100%' }}
                                        >
                                            {availablePassengers
                                                .filter(p => p.id !== parseInt(formData.passenger_id) && p.id !== userData?.id)
                                                .filter(p => !stopPaxSearch[index] || p.full_name.toLowerCase().includes(stopPaxSearch[index].toLowerCase()))
                                                .map(p => (
                                                    <div
                                                        key={p.id}
                                                        className="px-3 py-1.5 small text-dark hover-bg-light"
                                                        style={{ cursor: 'pointer' }}
                                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                                                        onMouseDown={() => {
                                                            const newStops = [...formData.stops];
                                                            newStops[index].passenger_name = p.full_name;
                                                            newStops[index].passenger_phone = p.phone || '';
                                                            setFormData({ ...formData, stops: newStops });
                                                            
                                                            setStopPaxSearch(prev => ({ ...prev, [index]: '' }));
                                                            setShowStopPaxDropdown(prev => ({ ...prev, [index]: false }));
                                                        }}
                                                    >
                                                        <strong>{p.full_name}</strong>
                                                        {p.phone && <span className="text-muted ms-2">({p.phone})</span>}
                                                    </div>
                                                ))
                                            }
                                            {((stopPaxSearch[index] || '').trim() !== '') && !availablePassengers.some(p => p.full_name.toLowerCase().includes(stopPaxSearch[index].toLowerCase())) && (
                                                <div className="px-3 py-2 text-muted small">Sin resultados</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Form.Group>
                ))}
                <div className="text-center mb-3 d-flex justify-content-center align-items-center gap-3">
                    <Button variant="link" size="sm" onClick={handleAddStop} className="text-decoration-none">+ Agregar Parada</Button>
                    {formData.stops.length > 1 && (
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={optimizeStopsOrder}
                            className="small"
                        >
                            🧠 Calcular Ruta Inteligente
                        </Button>
                    )}
                </div>

                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-end mb-1">
                      <Form.Label className="mb-0">Dirección de Destino <span className="text-danger">*</span></Form.Label>
                      <QuickButtons target="dropoff" />
                  </div>
                  <AddressAutocomplete 
                      value={formData.dropoff_address}
                      onChange={(val) => setFormData(prev => ({ ...prev, dropoff_address: val }))}
                      onSelect={handleDropoffSelect}
                      placeholder="Ej: Aeropuerto SCL"
                  />
                  {isDropoffAirport && (
                      <Alert variant="warning" className="py-2 px-3 small mt-2 mb-0">
                          ℹ️ Recuerda que para vuelos nacionales, debes estar con 2 horas de antelación, para vuelos internacionales, 3 horas.
                      </Alert>
                  )}
                </Form.Group>

                {minAdvanceMinutes > 0 && (
                    <Alert variant="info" className="py-2 px-3 small mb-3">
                        ⏱️ Su empresa requiere una anticipación mínima de{' '}
                        <strong>
                            {minAdvanceMinutes >= 60
                                ? `${Math.floor(minAdvanceMinutes / 60)}h${minAdvanceMinutes % 60 > 0 ? ` ${minAdvanceMinutes % 60}min` : ''}`
                                : `${minAdvanceMinutes} minutos`
                            }
                        </strong>{' '}
                        para solicitar un servicio.
                    </Alert>
                )}

                <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                          <Form.Label>Fecha <span className="text-danger">*</span></Form.Label>
                          <Form.Control 
                              type="date" 
                              min={todayStr}
                              value={formData.scheduled_date}
                              onChange={e => setFormData({...formData, scheduled_date: e.target.value})}
                              required
                          />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                          <Form.Label>Hora <span className="text-danger">*</span></Form.Label>
                          <div className="d-flex gap-2">
                              <Form.Select 
                                  value={formData.scheduled_time ? formData.scheduled_time.split(':')[0] : '08'}
                                  onChange={handleTimeHourChange}
                                  required
                              >
                                  {Array.from({ length: 24 }).map((_, i) => {
                                      const val = String(i).padStart(2, '0');
                                      return <option key={val} value={val}>{val}</option>;
                                  })}
                              </Form.Select>
                              <InputGroup.Text>:</InputGroup.Text>
                              <Form.Select 
                                  value={formData.scheduled_time ? (formData.scheduled_time.split(':')[1] || '00') : '00'}
                                  onChange={handleTimeMinuteChange}
                                  required
                              >
                                  {Array.from({ length: 60 }).map((_, i) => {
                                      const val = String(i).padStart(2, '0');
                                      return <option key={val} value={val}>{val}</option>;
                                  })}
                              </Form.Select>
                          </div>
                      </Form.Group>
                    </Col>
                </Row>

                 {dateTimeValidationError && (
                     <Alert variant="danger" className="py-2 px-3 small mb-3">
                         ⚠️ {dateTimeValidationError}
                     </Alert>
                 )}

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Tipo de Vehículo <span className="text-danger">*</span></Form.Label>
                  
                  {loadingQuotes ? (
                      <div className="d-flex align-items-center gap-2 text-primary p-2">
                          <Spinner animation="border" size="sm" />
                          <span className="small">Calculando tarifas de traslado...</span>
                      </div>
                  ) : availableQuotes.length > 0 ? (
                      <div>
                          <div className="text-muted small mb-2">Selecciona la opción de traslado para tu viaje:</div>
                          <div className="d-flex gap-2 overflow-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
                              {availableQuotes.map(q => {
                                  const isSelected = parseInt(formData.vehicle_type_id) === q.vehicle_type_id;

                                  // Pasajeros humanos + efectivos (mascota suma 1)
                                  const hasPet = (formData.extra_services || []).some(s => s.id === 'pet');
                                  const hasExtraLuggage = (formData.extra_services || []).some(s => s.id === 'extra_luggage');
                                  const humanPax = formData.additional_passenger_ids.length + 1 + (formData.stops || []).filter(s => s.passenger_name && s.passenger_name.trim() !== '').length;
                                  const effectivePax = humanPax + (hasPet ? 1 : 0);
                                  const stdMax = q.max_pax || 2;

                                  // Ocultar Standard solo si la capacidad se SUPERA:
                                  // - efectivePax > max (ej: 2 humanos + mascota = 3 > 2 → ocultar)
                                  // - maleta extra cuando ya hay 2 pax humanos (2 pax + maleta extra no caben)
                                  const isStandard = (q.vehicle_name || '').toLowerCase().includes('standard') || stdMax <= 2;
                                  const standardExcluded = isStandard && (
                                      effectivePax > stdMax ||
                                      (hasExtraLuggage && humanPax >= stdMax)
                                  );
                                  if (standardExcluded) return null;

                                  // Calcular precio con extras
                                  const extrasPrice = (formData.extra_services || []).reduce((sum, s) => {
                                      const opt = EXTRA_OPTIONS.find(o => o.id === s.id);
                                      return sum + (opt?.extraPrice || 0);
                                  }, 0);
                                  const displayPrice = Math.round((q.final_price || 0) + extrasPrice);

                                  return (
                                      <Card 
                                          key={q.vehicle_type_id}
                                          className={`flex-shrink-0 cursor-pointer shadow-sm border-2 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                                          style={{ width: '150px' }}
                                          onClick={() => setFormData(prev => ({ ...prev, vehicle_type_id: q.vehicle_type_id.toString() }))}
                                      >
                                          <Card.Body className="p-2 text-center d-flex flex-column justify-content-between" style={{ minHeight: '120px' }}>
                                              <div>
                                                  <Car size={24} className={isSelected ? 'text-primary' : 'text-muted'} />
                                                  <div className="fw-bold small mt-1 text-truncate" title={q.vehicle_name}>
                                                      {q.vehicle_name}
                                                  </div>
                                                  <div className="text-muted" style={{ fontSize: '10px' }}>
                                                       👤 {q.max_pax || '-'} | 🧳 {q.max_suitcases || '-'}
                                                  </div>
                                              </div>
                                              <div>
                                                  <div className="fw-bold text-dark mt-2" style={{ fontSize: '12px' }}>
                                                      ${displayPrice.toLocaleString()}
                                                  </div>
                                                  <div className="text-muted small" style={{ fontSize: '9px' }}>
                                                      Valor Venta
                                                  </div>
                                              </div>
                                          </Card.Body>
                                      </Card>
                                  );
                              })}
                          </div>
                          <div className="text-center mt-2 mb-1">
                              <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1" style={{ fontSize: '11px' }}>
                                  ⚠️ Valores Sujetos a Confirmación
                              </span>
                          </div>
                      </div>
                  ) : (
                      <Alert variant="info" className="py-2 px-3 small mb-0">
                          <Info size={14} className="me-2"/>
                          {formData.pickup_address && formData.dropoff_address && formData.scheduled_date && formData.scheduled_time 
                              ? "No hay vehículos disponibles para la capacidad o ruta solicitada." 
                              : "Ingrese origen, destino, fecha y hora para cargar las opciones disponibles."
                          }
                      </Alert>
                  )}
                </Form.Group>

                {isPickupAirport && !isServiceFarAhead && (
                    <Alert variant="warning" className="py-2 px-3 small mb-3">
                        ✈️ <strong>Recogida en Aeropuerto detectada.</strong> El número de vuelo es obligatorio para coordinar el servicio.
                    </Alert>
                )}
                {isPickupAirport && isServiceFarAhead && (
                    <Alert variant="info" className="py-2 px-3 small mb-3">
                        ✈️ Recogida en Aeropuerto. Como el servicio es en más de 2 días, el número de vuelo es opcional por ahora.
                    </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Número de Vuelo {flightRequired && <span className="text-danger">*</span>}</Form.Label>
                  <Form.Control 
                      type="text" 
                      placeholder="Ej: LA530" 
                      value={formData.flight_number || ''}
                      onChange={e => setFormData({...formData, flight_number: e.target.value})}
                  />
                  {isPickupAirport && !flightRequired && (
                      <Form.Text className="text-muted">Opcional para servicios con más de 2 días de anticipación.</Form.Text>
                  )}
                </Form.Group>

                {user.role === 'requester' && (
                    <Row className="mb-3 bg-info bg-opacity-10 p-2 rounded mx-0">
                        <Col md={12}>
                            <Form.Label className="small fw-bold">Nro File / OC</Form.Label>
                            <Form.Control size="sm" value={formData.client_file_number} onChange={e => setFormData({...formData, client_file_number: e.target.value})} placeholder="ID Cliente" />
                        </Col>
                    </Row>
                )}

                <h6 className="text-muted mb-3 mt-4">Servicios Adicionales (Extras)</h6>
                <div className="p-3 border rounded bg-light mb-3">
                    <Row className="g-2">
                        {EXTRA_OPTIONS.map(opt => {
                            const isChecked = (formData.extra_services || []).some(s => s.id === opt.id);
                            const priceLabel = opt.extraPrice > 0 ? ` (+$${opt.extraPrice.toLocaleString()})` : '';
                            return (
                                <Col md={6} key={opt.id}>
                                    <Form.Check 
                                        type="checkbox"
                                        id={`extra-opt-${opt.id}`}
                                        label={<span>{opt.name}<span className="text-success small">{priceLabel}</span></span>}
                                        checked={isChecked}
                                        onChange={() => handleToggleExtra(opt.id, opt.name)}
                                    />
                                </Col>
                            );
                        })}
                    </Row>
                    {/* Aviso canil si se marca mascota */}
                    {(formData.extra_services || []).some(s => s.id === 'pet') && (
                        <Alert variant="warning" className="py-2 px-3 small mt-2 mb-0">
                            🐾 <strong>Recuerda que debes llevarlo en su respectivo canil.</strong>
                        </Alert>
                    )}
                </div>

                <Form.Group className="mb-4">
                  <Form.Label>Notas Adicionales</Form.Label>
                  <Form.Control 
                      as="textarea" 
                      rows={2} 
                      placeholder="Ej: Maletas grandes, esperar en lobby..." 
                      value={formData.notes}
                      onChange={e => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>

                {isCreatingReturn && (
                  <Alert variant="warning" className="mb-3 d-flex align-items-center gap-2 py-2">
                    <span style={{ fontSize: '1.3rem' }}>🔄</span>
                    <div>
                      <div className="fw-bold">Modo Retorno</div>
                      <div className="small">Las direcciones se han invertido. Ingresa la fecha y hora del regreso.</div>
                    </div>
                  </Alert>
                )}
                <div className="d-grid gap-2">
                  <Button variant={isEditing ? "success" : "primary"} type="submit" size="lg" disabled={submitting} className="fw-bold">
                    {submitting 
                      ? (isEditing ? 'Guardando...' : 'Solicitando...') 
                      : (isEditing 
                          ? `💾 Guardar Cambios en Reserva #${editTrip.id}` 
                          : (isCreatingReturn ? '✅ Confirmar Retorno' : 'Confirmar Solicitud'))
                    }
                  </Button>
                  {!isCreatingReturn && !isEditing && (
                    <Button
                      variant="outline-primary"
                      size="lg"
                      type="button"
                      disabled={submitting}
                      onClick={() => handleSubmit(null, true)}
                    >
                      🔄 Guardar y Crear Retorno
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow-sm border-0 h-100" style={{ minHeight: '500px' }}>
            <Card.Body className="p-0 position-relative h-100">
              <RouteMap 
                pickupLocation={formData.pickup_lat && formData.pickup_lng ? { lat: formData.pickup_lat, lng: formData.pickup_lng } : null}
                dropoffLocation={formData.dropoff_lat && formData.dropoff_lng ? { lat: formData.dropoff_lat, lng: formData.dropoff_lng } : null}
                stops={formData.stops}
                onDistanceCalculated={handleDistanceCalculated}
              />
              
              {formData.distance_km > 0 && (
                  <div className="position-absolute bottom-0 start-0 m-3 p-3 bg-white rounded shadow-sm border border-light" style={{ zIndex: 10, width: 'calc(100% - 30px)' }}>
                      <Row className="text-center">
                          <Col border="end">
                              <div className="text-muted small">Distancia</div>
                              <div className="fw-bold">{formData.distance_km.toFixed(1)} km</div>
                          </Col>
                          <Col>
                              <div className="text-muted small">Duración Est.</div>
                              <div className="fw-bold">{formData.estimated_duration}</div>
                          </Col>
                      </Row>
                  </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick Create Modals */}
      <Modal show={showAreaModal} onHide={() => setShowAreaModal(false)} size="sm">
          <Modal.Header closeButton><Modal.Title>Nueva Área</Modal.Title></Modal.Header>
          <Modal.Body>
              <Form.Control placeholder="Nombre" value={newItemName} onChange={e => setNewItemName(e.target.value)} className="mb-2"/>
              <Button onClick={handleCreateArea} className="w-100">Crear</Button>
          </Modal.Body>
      </Modal>

      <Modal show={showCCModal} onHide={() => setShowCCModal(false)} size="sm">
          <Modal.Header closeButton><Modal.Title>Nuevo Centro de Costo</Modal.Title></Modal.Header>
          <Modal.Body>
              {/* FIX 5: Autocompletado para detectar si ya existe */}
              {costCenters.length > 0 && (newItemCode || newItemName) && (() => {
                  const similar = costCenters.filter(cc =>
                      (cc.code || '').toLowerCase().includes(newItemCode.toLowerCase()) ||
                      (cc.name || '').toLowerCase().includes(newItemName.toLowerCase())
                  ).slice(0, 3);
                  return similar.length > 0 ? (
                      <Alert variant="warning" className="py-2 px-3 small mb-2">
                          ⚠️ Ya existen centros similares:
                          {similar.map(cc => (
                              <div key={cc.id} className="fw-bold">{cc.code} - {cc.name}</div>
                          ))}
                      </Alert>
                  ) : null;
              })()}
              <Form.Label className="small">Código *</Form.Label>
              <Form.Control
                  placeholder="Ej: CC-001"
                  value={newItemCode}
                  onChange={e => setNewItemCode(e.target.value)}
                  className="mb-2"
                  autoFocus
              />
              <Form.Label className="small">Nombre</Form.Label>
              <Form.Control
                  placeholder="Ej: Gerencia de Operaciones"
                  value={newItemName}
                  onChange={e => setNewItemName(e.target.value)}
                  className="mb-3"
              />
              <Button onClick={handleCreateCCForTarget} className="w-100" disabled={!newItemCode}>
                  Crear Centro de Costo
              </Button>
          </Modal.Body>
      </Modal>

      {/* Quick Passenger Modal (Sin opción de acceso al portal) */}
      <Modal show={showQuickPaxModal} onHide={() => setShowQuickPaxModal(false)} size="md">
          <Modal.Header closeButton>
              <Modal.Title className="h6 fw-bold">Añadir Pasajero Frecuente</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <Row className="g-2">
                  <Col md={12}>
                      <Form.Label className="small">Nombre Completo</Form.Label>
                      <InputGroup size="sm">
                          <InputGroup.Text><User size={14}/></InputGroup.Text>
                          <Form.Control 
                              placeholder="Ej: Juan Pérez" 
                              value={quickPax.full_name} 
                              onChange={e => setQuickPax({...quickPax, full_name: e.target.value})}
                          />
                      </InputGroup>
                  </Col>
                  <Col md={6}>
                      <Form.Label className="small">Email</Form.Label>
                      <InputGroup size="sm">
                          <InputGroup.Text><Mail size={14}/></InputGroup.Text>
                          <Form.Control 
                              placeholder="juan@empresa.cl" 
                              value={quickPax.email} 
                              onChange={e => setQuickPax({...quickPax, email: e.target.value})}
                          />
                      </InputGroup>
                  </Col>
                  <Col md={6}>
                      <Form.Label className="small">Teléfono</Form.Label>
                      <InputGroup size="sm">
                          <InputGroup.Text><Phone size={14}/></InputGroup.Text>
                          <Form.Control 
                              placeholder="+569..." 
                              value={quickPax.phone} 
                              onChange={e => setQuickPax({...quickPax, phone: e.target.value})}
                          />
                      </InputGroup>
                  </Col>
                  
                  <Col md={6}>
                      <Form.Label className="small">Área</Form.Label>
                      <div className="d-flex gap-1">
                          <Form.Select size="sm" value={quickPax.area_id || ''} onChange={e => setQuickPax({...quickPax, area_id: e.target.value})}>
                              <option value="">Sin Área</option>
                              {areas
                                  .filter(a => a.is_active !== false || String(a.id) === String(quickPax.area_id))
                                  .map(a => (
                                      <option key={a.id} value={a.id}>
                                          {a.name} {a.is_active === false ? " (Inactivo)" : ""}
                                      </option>
                                  ))
                              }
                          </Form.Select>
                          <Button variant="outline-primary" size="sm" onClick={() => openAreaModal('pax')} title="Nueva Área">
                              <Plus size={14}/>
                          </Button>
                      </div>
                  </Col>
                  <Col md={6}>
                      <Form.Label className="small">Centro de Costo</Form.Label>
                      <div className="d-flex gap-1">
                          <Form.Select size="sm" value={quickPax.cost_center_id || ''} onChange={e => setQuickPax({...quickPax, cost_center_id: e.target.value})}>
                              <option value="">Sin CC</option>
                              {costCenters
                                  .filter(cc => cc.is_active !== false || String(cc.id) === String(quickPax.cost_center_id))
                                  .map(cc => (
                                      <option key={cc.id} value={cc.id}>
                                          {cc.code} - {cc.name} {cc.is_active === false ? " (Inactivo)" : ""}
                                      </option>
                                  ))
                              }
                          </Form.Select>
                          {/* FIX 5: usar modal unificado en vez de window.prompt */}
                          <Button variant="outline-primary" size="sm" onClick={() => openCCModal('pax')} title="Nuevo CC">
                              <Plus size={14}/>
                          </Button>
                      </div>
                  </Col>

                  <Col md={12}>
                      <Form.Label className="small">Dirección Particular (Casa)</Form.Label>
                      <AddressAutocomplete 
                          value={quickPax.home_address || ''}
                          onChange={(val) => setQuickPax(prev => ({ ...prev, home_address: val }))}
                          onSelect={({ address }) => setQuickPax(prev => ({ ...prev, home_address: address }))}
                          placeholder="Ej: Av. El Bosque 123"
                      />
                  </Col>
                  <Col md={12}>
                      <Form.Label className="small">Dirección Laboral (Oficina)</Form.Label>
                      <AddressAutocomplete 
                          value={quickPax.office_address || ''}
                          onChange={(val) => setQuickPax(prev => ({ ...prev, office_address: val }))}
                          onSelect={({ address }) => setQuickPax(prev => ({ ...prev, office_address: address }))}
                          placeholder="Ej: Av. Providencia 456"
                      />
                  </Col>
              </Row>
              <Button className="w-100 mt-3 fw-bold shadow-sm" onClick={handleQuickCreatePassenger}>Crear Pasajero</Button>
          </Modal.Body>
      </Modal>

      {/* Airport Selection Modal */}
      <Modal show={showAirportModal} onHide={() => setShowAirportModal(false)} size="sm">
          <Modal.Header closeButton className="py-2">
              <Modal.Title className="h6 fw-bold">Seleccionar Aeropuerto</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <div className="d-grid gap-2">
                  {AIRPORTS.map(a => (
                      <Button 
                          key={a.code} 
                          variant="outline-primary" 
                          size="sm" 
                          className="text-start" 
                          onClick={() => handleAirportSelect(a)}
                      >
                          <Plane size={14} className="me-2"/>
                          {a.code} - {a.name}
                      </Button>
                  ))}
              </div>
          </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewTripPage;